import type { FoodDefinition } from '@/types';

/** Food/treat roster. Sprites are AI-illustrated PNGs (see foodTextureKey/PreloadScene);
 * `color` only drives small non-gameplay UI touches like chart editor minimap dots. */
export const FOODS: FoodDefinition[] = [
  { id: 'iceCream', name: 'Ice Cream', color: 0xffc7de },
  { id: 'fish', name: 'Fish', color: 0x7ec8e3 },
  { id: 'donut', name: 'Donut', color: 0xd98a5f },
  { id: 'cupcake', name: 'Cupcake', color: 0xf7d6e0 },
  { id: 'candy', name: 'Candy', color: 0xff5c8a },
  { id: 'sushi', name: 'Sushi', color: 0xffffff },
];

export function getFoodById(id: string): FoodDefinition {
  const food = FOODS.find((f) => f.id === id);
  if (!food) throw new Error(`Unknown food id: ${id}`);
  return food;
}

/** Texture key for a treat's AI-illustrated sprite, shared between PreloadScene
 * (loading) and FoodNote/TreatSelectScene (rendering). */
export function foodTextureKey(foodId: string): string {
  return `food-${foodId}`;
}

/** Resolves the food a chart note should actually spawn as, honoring the player's
 * enabled-treats configuration. Charts are generated once and bake in a fixed food id
 * per note (see chartGenerator.ts), so disabling a treat can't remove notes from the
 * chart -- instead each disabled note deterministically remaps to an enabled treat via
 * its original index, keeping the same chart shape/variety across replays regardless
 * of which treats are currently enabled. */
export function resolveFoodForNote(originalFoodId: string, enabledFoodIds: string[]): FoodDefinition {
  if (enabledFoodIds.includes(originalFoodId)) return getFoodById(originalFoodId);
  const originalIndex = FOODS.findIndex((f) => f.id === originalFoodId);
  const enabled = FOODS.filter((f) => enabledFoodIds.includes(f.id));
  if (enabled.length === 0) return getFoodById(originalFoodId);
  return enabled[Math.max(originalIndex, 0) % enabled.length];
}
